import { Component } from '@theme/component';

const ALTITUDE_START = 30000;

/**
 * Cinema video player for the QR landing pages.
 * Layout (aspect ratio, size) is fully handled by Liquid + CSS; this component only
 * drives the variant tabs and the decorative meta bar under each video.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 *
 * @extends {Component<Refs>}
 */
class PillowQrVideo extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  #numberFormat = new Intl.NumberFormat(document.documentElement.lang || undefined);

  connectedCallback() {
    super.connectedCallback();
    this.#bindVideos();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindVideos();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * @param {number} index - 1-based variant index
   */
  selectVariant(index) {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];

    tabs.forEach((tab, i) => {
      const active = i + 1 === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    panels.forEach((panel, i) => {
      const active = i + 1 === index;
      if (!active) panel.querySelector('video')?.pause();
      panel.hidden = !active;
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  onTabKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;

    let next;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    this.selectVariant(next + 1);
    tabs[next]?.focus();
  }

  #bindVideos() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    for (const panel of this.refs.panels ?? []) {
      const video = panel.querySelector('video');
      if (!video) continue;

      video.addEventListener('timeupdate', () => this.#updateMeta(panel, video), { signal });
      // Safari reports 0×0 at loadedmetadata for HLS streams; real dimensions arrive with `resize`
      video.addEventListener('loadedmetadata', () => this.#syncRatio(panel, video), { signal });
      video.addEventListener('resize', () => this.#syncRatio(panel, video), { signal });

      if (video.readyState >= HTMLMediaElement.HAVE_METADATA) this.#syncRatio(panel, video);
      this.#updateMeta(panel, video);
    }
  }

  /**
   * Safety net only: Liquid already sets the ratio. If the file's real dimensions differ, follow them.
   * @param {HTMLElement} panel
   * @param {HTMLVideoElement} video
   */
  #syncRatio(panel, video) {
    const cinema = panel.querySelector('.pqv__cinema');
    if (!(cinema instanceof HTMLElement) || !video.videoWidth || !video.videoHeight) return;
    cinema.style.setProperty('--pqv-ratio', `${video.videoWidth} / ${video.videoHeight}`);
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLVideoElement} video
   */
  #updateMeta(panel, video) {
    const timeEl = panel.querySelector('[data-time-current]');
    if (timeEl) timeEl.textContent = this.#formatTime(video.currentTime);

    const altitudeEl = panel.querySelector('[data-altitude]');
    if (!(altitudeEl instanceof HTMLElement)) return;

    const label = altitudeEl.dataset.label ?? '';
    if (!label.includes('{n}')) return;

    const progress = video.duration ? video.currentTime / video.duration : 0;
    const altitude = Math.round(((1 - progress) * ALTITUDE_START) / 100) * 100;
    altitudeEl.textContent = label.replace('{n}', this.#numberFormat.format(altitude));
  }

  /**
   * @param {number} seconds
   */
  #formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${m}:${s}`;
  }
}

if (!customElements.get('pillow-qr-video')) {
  customElements.define('pillow-qr-video', PillowQrVideo);
}
